import React, { useState, useEffect } from 'react';
import { downloadSubmissionsAsCSV, getAllUserSubmissions } from '../utils/exportToSheets';
import { useLanguage } from '../i18n/useLanguage.jsx';

export default function AdminPanel() {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submissions, setSubmissions] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const ADMIN_PASSWORD = 'Pass1234';

  const handleLoadSubmissions = async (isRefresh = false) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const submissionsData = await getAllUserSubmissions();
      setSubmissions(submissionsData);
      setSuccess(t('adminPanel.totalSubmissions', { count: submissionsData.length }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-load submissions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      handleLoadSubmissions();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      setPassword(''); // Clear password from memory
    } else {
      setAuthError('Incorrect password');
      setPassword('');
    }
  };

  const handleDownloadCSV = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const count = await downloadSubmissionsAsCSV();
      setSuccess(t('adminPanel.downloadSuccess', { count }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

    // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <div className="admin-panel-overlay">
        <h3 className="admin-panel-header login-header">{t('adminPanel.loginTitle')}</h3>
        
        <form onSubmit={handleLogin}>
          <div className="admin-login-input-container">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('adminPanel.passwordPlaceholder')}
              className={`admin-login-input ${authError ? 'error' : ''}`}
              autoFocus
            />
          </div>
          
          <button 
            type="submit"
            className="ui-congrats-btn admin-login-btn"
          >
            {t('adminPanel.loginButton')}
          </button>
        </form>

        {authError && (
          <div className="admin-error-message">
            {authError === 'Incorrect password' ? t('adminPanel.incorrectPassword') : authError}
          </div>
        )}
        
        <div className="admin-info-text">
          {t('adminPanel.adminRequired')}
        </div>
      </div>
    );
  }

  // If authenticated, show admin panel
  return (
    <div className={`admin-panel-overlay ${isCollapsed ? 'collapsed' : ''}`}>

      {/* Header - always visible */}
      <h3 className="admin-panel-header">
        <span>{t('adminPanel.panelTitle')}</span>
        <div className="admin-panel-controls">
          {!isCollapsed && (
            <button 
              onClick={() => handleLoadSubmissions(true)}
              disabled={isLoading}
              className="ui-congrats-btn admin-refresh-btn"
            >
              {isLoading ? `${t('adminPanel.loadingButton')}` : `${t('adminPanel.refreshButton')}`}
            </button>
          )}
          <div 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`admin-toggle-btn ${isCollapsed ? 'collapsed' : 'expanded'}`}
            title={isCollapsed ? t('adminPanel.expandPanel') : t('adminPanel.collapsePanel')}
          >
            <span className="toggle-symbol"></span>
          </div>
        </div>
      </h3>

      {/* Panel Content - conditionally rendered */}
      {!isCollapsed && (
        <>

          {submissions && (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('adminPanel.dateTimeHeader')}</th>
                    <th>{t('adminPanel.nameHeader')}</th>
                    <th>{t('adminPanel.emailHeader')}</th>
                    <th>{t('adminPanel.answerHeader')}</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission, index) => {
                    return (
                      <tr key={submission.id}>
                        <td className="timestamp">
                          {submission.createdAt || 'N/A'}
                        </td>
                        <td className="name">
                          {submission.name}
                        </td>
                        <td className="email">
                          {submission.email}
                        </td>
                        <td className="answer">
                          {submission.answer}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {submissions.length === 0 && (
                <div className="admin-no-submissions">
                  {t('adminPanel.noSubmissions')}
                </div>
              )}
            </div>
          )}

          {success && (
            <div className="admin-success-message">
              <span>{success}</span>
              <div className="admin-success-controls">
                <button 
                  onClick={handleDownloadCSV}
                  disabled={isLoading}
                  className="admin-download-btn"
                >
                  {isLoading ? `${t('adminPanel.loadingButton')}` : `${t('adminPanel.downloadButton')}`}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="admin-error-panel">
              {error}
              
              {error.includes('Permission denied') && (
                <div className="admin-error-fix">
                  <strong>Fix:</strong> Update your Firestore rules to allow reads:
                  <br />
                  <code>allow read: if true;</code>
                </div>
              )}
            </div>
          )}
          
          <div className="admin-footer-info">
            <strong>{t('adminPanel.usageLabel')}</strong> {t('adminPanel.usageText')}
            <br />
            <strong>{t('adminPanel.securityLabel')}</strong> {t('adminPanel.securityText')}
          </div>
        </>
      )}
    </div>
  );
}